import { Component, Input, OnInit } from '@angular/core';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { ConfirmationService } from 'primeng/api';
import { Globals } from '../../../../global/globals';
import { WorkflowInstanceService } from '../../../../services/workflow-instance.service';
import { WorkflowDetailClientService } from '../../../dialog/workflow-detail-dialog/workflow-detail-client.service';
import { WorkflowStep } from '../../../../enums/WorkflowStep';
import { WorkflowStepReviewModel } from '../../../../models/workflow-step/workflow-step-review.model';
import {
  WorkflowInstanceStepReviewEditModel
} from '../../../../models/workflow-step/edit/workflow-instance-step-review-edit.model';
import { MessageService } from '../../../../services/message.service';
import { AuthenticationService } from '../../../../services/authentication.service';

@Component({
  selector: 'app-review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss']
})
export class ReviewComponent implements OnInit {

  @Input() workFlowStep: WorkflowStepReviewModel;

  expandedSteps: Set<number> = new Set();
  loading = false;
  faCheck = faCheck;

  constructor(public globals: Globals,
              private workflowInstanceService: WorkflowInstanceService,
              public workflowDetailClientService: WorkflowDetailClientService,
              private messageService: MessageService,
              private authService: AuthenticationService,
              private confirmationService: ConfirmationService) {
  }

  ngOnInit(): void {
    if (this.isApproved) {
      this.workflowDetailClientService.isStepValid$.next(true);
    }
  }

  get uniqueLatestSteps() {
    const steps = this.workflowDetailClientService.workflowInstance?.steps || [];
    const seen = new Set<any>();
    const result: any[] = [];

    const allowedSteps = this.workflowDetailClientService.workflowInstance?.isLibrary() ?
      WorkflowStep.getAllForDashboardInLibrary().flat() :
      WorkflowStep.getAllForDashboardInWorkflow96And384().flat();

    // Iterate from the end to keep the newest occurrence for each step type
    for (let i = steps.length - 1; i >= 0; i--) {
      const s = steps[i];
      const key = s.step;
      if (!seen.has(key) && allowedSteps.includes(key)) {
        seen.add(key);
        result.push(s);
      }
    }

    // Reverse to preserve the original chronological order while keeping newest per type
    return result.reverse();
  }

  hasNonEmptyComment(comment: string | null | undefined): boolean {
    const c = comment != null ? comment.trim() : '';
    return c.length > 0;
  }

  commentStripped(comment: string, maxChars: number): string {
    return comment?.length > maxChars ? comment.substring(0, maxChars) + '...' : comment;
  }

  isExpanded(stepId: number): boolean {
    return this.expandedSteps.has(stepId);
  }

  toggleExpanded(stepId: number): void {
    if (this.isExpanded(stepId)) {
      this.expandedSteps.delete(stepId);
    } else {
      this.expandedSteps.add(stepId);
    }
  }

  approve(): void {
    if (this.isCreator) {
      this.confirmationService.confirm({
        header: 'Warnung: Eigenfreigabe',
        message: 'Sie haben diesen Workflow selbst abgeschlossen. Möchten Sie ihn dennoch freigeben?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Freigeben',
        rejectLabel: 'Abbrechen',
        accept: () => {
          // Delaying ensures the first dialog is fully closed before the second one opens
          // not nice, but the check this.isCreator should normally be false anyway
          setTimeout(() => this.confirmApproval(), 300);
        }
      });
    } else {
      this.confirmApproval();
    }
  }

  private confirmApproval(): void {
    this.confirmationService.confirm({
      header: 'Workflow freigeben',
      message: 'Sind Sie sicher, dass Sie diesen Workflow freigeben möchten?',
      icon: 'pi pi-check',
      acceptLabel: 'Freigeben',
      rejectLabel: 'Abbrechen',
      accept: () => {
        this.executeApprove();
      }
    });
  }

  private executeApprove(): void {
    this.loading = true;
    this.workflowInstanceService.approveWorkflowInstance(this.workflowDetailClientService.workflowInstance.id).subscribe({
      next: (updatedWorkflow) => {
        this.loading = false;
        this.workflowDetailClientService.workflowInstance = updatedWorkflow;
        this.workflowDetailClientService.isStepValid$.next(true);
        this.messageService.addSuccessMessage('Workflow erfolgreich freigegeben.');
      },
      error: (err) => {
        this.loading = false;
        this.messageService.addErrorMessage(err.error?.message || 'Fehler bei der Freigabe.');
      }
    });
  }

  get isCreator(): boolean {
    return this.workFlowStep?.finishedBy === this.authService.getUserName();
  }

  get isApproved(): boolean {
    return this.workflowDetailClientService.workflowInstance.approved;
  }

}
